import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import { oesApi, settingsApi } from '../../services/api';
import { getScaleLevels } from '../../utils/settingsScales';
import Layout from '../../components/layout/Layout';
import { Card } from '../../components/common/Card';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';
import AISuggestionReviewModal from '../../components/AISuggestionReviewModal';

const IT_OT_SYSTEMS = [
  'SCADA', 'DCS', 'PLC', 'HMI', 'RTU', 'IT Infrastructure', 'Information Systems',
  '4G/5G', 'LAN/WAN', 'Wireless Networks', 'DNS Systems', 'Core Banking', 'ATMs', 'Payment Processing',
  'ERP', 'CRM', 'DBMS', 'Cloud Services', 'Radar Systems', 'ATC (Air Traffic Control)',
  'Power Grid Systems', 'Water Management Systems', 'Transportation Systems', 'Medical Devices',
  'IoT Devices', 'Industrial Control Systems', 'Other'
];

const SCORE_COLORS = {
  1: 'bg-emerald-500', 2: 'bg-blue-400', 3: 'bg-amber-400', 4: 'bg-orange-500', 5: 'bg-red-500',
};

// Compact horizontal 1-5 score picker
function ScorePicker({ value, onChange, labels }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          title={labels?.[n - 1] || `${n}`}
          className={`w-9 h-8 rounded text-xs font-bold transition-all ${
            value === n
              ? `${SCORE_COLORS[n]} text-white shadow-sm scale-110`
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

// Collapsible section wrapper
function Section({ title, subtitle, expanded, onToggle, children, aiButton }) {
  return (
    <Card className="p-0 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 text-left"
      >
        <div>
          <h3 className="text-md font-semibold text-slate-700">{title}</h3>
          {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {aiButton}
          <span className="text-slate-400 text-xs">{expanded ? '▼' : '▶'}</span>
        </div>
      </button>
      {expanded && <div className="p-5 border-t border-slate-100">{children}</div>}
    </Card>
  );
}

export default function OESProfile() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const { oesId } = useParams();
  const [oes, setOes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [scales, setScales] = useState(null);

  // Existing assessment warning
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [submittedAssessment, setSubmittedAssessment] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // AI state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPhase, setAiPhase] = useState(''); // which phase is loading
  const [aiError, setAiError] = useState('');
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [aiReviewPhases, setAiReviewPhases] = useState(null); // single phase string or array for auto-profile
  const [aiSuggestion, setAiSuggestion] = useState(null);

  // Section visibility
  const [sections, setSections] = useState({ services: true, processes: true, assets: true, cyberattack: true });
  const toggleSection = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Form data (same shape as OESAssessment.jsx)
  const [services, setServices] = useState([
    { service_name: '', service_description: '', user_count_score: 0, frequency_score: 0, replaceability_score: 0, interdependency_score: 0, time_to_impact_score: 0 }
  ]);
  const [processes, setProcesses] = useState([
    { process_name: '', process_description: '', automation_level: 0, it_ot_systems: [] }
  ]);
  const [assetValuations, setAssetValuations] = useState({});
  const [cyberattackImpact, setCyberattackImpact] = useState({
    security_impact: 0, economy_impact: 0, health_impact: 0, privacy_impact: 0, notes: ''
  });

  // "Other" IT/OT custom input state
  const [otherInputProcessIndex, setOtherInputProcessIndex] = useState(null);
  const [otherInputValue, setOtherInputValue] = useState('');

  // Load OES details
  useEffect(() => {
    const load = async () => {
      try {
        const res = await oesApi.getById(oesId);
        setOes(res.data);
        // Try loading existing assessment to pre-populate
        try {
          const assessRes = await oesApi.getCurrentAssessment(oesId);
          if (assessRes.data) {
            setExistingAssessment(assessRes.data);
            prefillFromAssessment(assessRes.data);
          }
        } catch { /* no existing assessment — start fresh */ }
      } catch (err) {
        setError(err.response?.data?.detail || `Failed to load ${labels.entityName}`);
      } finally {
        setLoading(false);
      }
    };
    load();
    settingsApi.getForCurrentUser()
      .then(r => setScales(r.data?.scales ?? {}))
      .catch(() => setScales({}));
  }, [oesId]);

  const prefillFromAssessment = (a) => {
    if (a.services?.length) {
      setServices(a.services.map(s => ({
        service_name: s.service_name || '',
        service_description: s.service_description || '',
        user_count_score: s.user_count_score || 0,
        frequency_score: s.frequency_score || 0,
        replaceability_score: s.replaceability_score || 0,
        interdependency_score: s.interdependency_score || 0,
        time_to_impact_score: s.time_to_impact_score || 0,
      })));
    }
    if (a.processes?.length) {
      setProcesses(a.processes.map(p => ({
        process_name: p.process_name || '',
        process_description: p.process_description || '',
        automation_level: Number(p.automation_level) || 0,
        it_ot_systems: typeof p.it_ot_systems === 'string' ? p.it_ot_systems.split(',').map(s => s.trim()).filter(Boolean) : (p.it_ot_systems || []),
      })));
    }
    if (a.asset_valuations) setAssetValuations(a.asset_valuations);
    if (a.cyberattack_impact) {
      setCyberattackImpact({
        security_impact: a.cyberattack_impact.security_impact || 0,
        economy_impact: a.cyberattack_impact.economy_impact || 0,
        health_impact: a.cyberattack_impact.health_impact || 0,
        privacy_impact: a.cyberattack_impact.privacy_impact || 0,
        notes: a.cyberattack_impact.notes || '',
      });
    }
  };

  // Scale labels
  const userCountLabels = (getScaleLevels(scales, 'user_count_levels') || []).map(l => l.label);
  const frequencyLabels = (getScaleLevels(scales, 'frequency_levels') || []).map(l => l.label);
  const replaceabilityLabels = (getScaleLevels(scales, 'replaceability_levels') || []).map(l => l.label);
  const interdependencyLabels = (getScaleLevels(scales, 'interdependency_levels') || []).map(l => l.label);
  const timeToImpactLabels = (getScaleLevels(scales, 'time_to_impact_levels') || []).map(l => l.label);
  const automationLabels = (getScaleLevels(scales, 'automation_levels') || []).map(l => l.label);
  const cyberLabels = (getScaleLevels(scales, 'cyberattack_impact_levels') || []).map(l => l.label);

  // Mark dirty on any form change
  const markDirty = () => { if (!isDirty) setIsDirty(true); };

  // Service helpers
  const updateService = (idx, field, value) => {
    setServices(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
    markDirty();
  };
  const addService = () => {
    if (services.length < 5) { setServices(prev => [...prev, { service_name: '', service_description: '', user_count_score: 0, frequency_score: 0, replaceability_score: 0, interdependency_score: 0, time_to_impact_score: 0 }]); markDirty(); }
  };
  const removeService = (idx) => {
    if (services.length > 1) { setServices(prev => prev.filter((_, i) => i !== idx)); markDirty(); }
  };

  // Process helpers
  const updateProcess = (idx, field, value) => {
    setProcesses(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
    markDirty();
  };
  const addProcess = () => {
    if (processes.length < 5) { setProcesses(prev => [...prev, { process_name: '', process_description: '', automation_level: 0, it_ot_systems: [] }]); markDirty(); }
  };
  const removeProcess = (idx) => {
    if (processes.length > 1) { setProcesses(prev => prev.filter((_, i) => i !== idx)); markDirty(); }
  };
  const toggleItOt = (procIdx, system) => {
    if (system === 'Other') {
      setOtherInputProcessIndex(procIdx);
      setOtherInputValue('');
      return;
    }
    setProcesses(prev => prev.map((p, i) => {
      if (i !== procIdx) return p;
      const has = p.it_ot_systems.includes(system);
      return { ...p, it_ot_systems: has ? p.it_ot_systems.filter(s => s !== system) : [...p.it_ot_systems, system] };
    }));
    markDirty();
  };
  const addCustomItOt = (procIdx) => {
    const val = otherInputValue.trim();
    if (!val) return;
    setProcesses(prev => prev.map((p, i) => i === procIdx && !p.it_ot_systems.includes(val) ? { ...p, it_ot_systems: [...p.it_ot_systems, val] } : p));
    setOtherInputProcessIndex(null);
    setOtherInputValue('');
    markDirty();
  };

  // Distinct assets from processes
  const getDistinctAssets = () => {
    const seen = new Set();
    processes.forEach(p => (p.it_ot_systems || []).forEach(name => { if (name?.trim()) seen.add(name.trim()); }));
    return Array.from(seen);
  };

  // Normalize AI response into phase data shape
  const normalizeAiResponse = (phase, data) => {
    if (phase === 'services' && data.services?.length) {
      return data.services.map(s => ({
        service_name: s.service_name || '',
        service_description: s.service_description || '',
        user_count_score: s.user_count_score || 0,
        frequency_score: s.frequency_score || 0,
        replaceability_score: s.replaceability_score || 0,
        interdependency_score: s.interdependency_score || 0,
        time_to_impact_score: s.time_to_impact_score || 0,
      }));
    }
    if (phase === 'processes' && data.processes?.length) {
      return data.processes.map(p => ({
        process_name: p.process_name || '',
        process_description: p.process_description || '',
        automation_level: p.automation_level || 0,
        it_ot_systems: Array.isArray(p.it_ot_systems) ? p.it_ot_systems : (typeof p.it_ot_systems === 'string' ? p.it_ot_systems.split(',').map(s => s.trim()).filter(Boolean) : []),
      }));
    }
    if (phase === 'cyberattack' && data) {
      return {
        security_impact: data.security_impact ?? 0,
        economy_impact: data.economy_impact ?? 0,
        health_impact: data.health_impact ?? 0,
        privacy_impact: data.privacy_impact ?? 0,
        notes: data.notes ?? '',
      };
    }
    return null;
  };

  // AI handlers — store results in temp state and open review modal
  const handleAiPhase = async (phase) => {
    if (!oesId || aiLoading) return;
    setAiError('');
    setAiLoading(true);
    setAiPhase(phase);
    try {
      const res = await oesApi.aiSuggest(oesId, phase);
      const normalized = normalizeAiResponse(phase, res.data);
      if (normalized) {
        setAiReviewPhases(phase);
        setAiSuggestion({ [phase]: normalized });
        setAiReviewOpen(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'AI suggestion failed.';
      setAiError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setAiLoading(false);
      setAiPhase('');
    }
  };

  const handleAutoProfile = async () => {
    if (aiLoading) return;
    setAiError('');
    const allSuggestions = {};
    const completedPhases = [];
    for (const phase of ['services', 'processes', 'cyberattack']) {
      setAiLoading(true);
      setAiPhase(phase);
      try {
        const res = await oesApi.aiSuggest(oesId, phase);
        const normalized = normalizeAiResponse(phase, res.data);
        if (normalized) {
          allSuggestions[phase] = normalized;
          completedPhases.push(phase);
        }
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'AI suggestion failed.';
        setAiError(typeof detail === 'string' ? detail : JSON.stringify(detail));
        break;
      }
    }
    setAiLoading(false);
    setAiPhase('');
    if (completedPhases.length > 0) {
      setAiReviewPhases(completedPhases);
      setAiSuggestion(allSuggestions);
      setAiReviewOpen(true);
    }
  };

  const handleApplyAiSuggestion = (merged) => {
    if (merged.services) setServices(merged.services);
    if (merged.processes) setProcesses(merged.processes);
    if (merged.cyberattack) setCyberattackImpact(merged.cyberattack);
    setAiReviewOpen(false);
    setAiSuggestion(null);
    setAiReviewPhases(null);
    markDirty();
  };

  const getAiReviewCurrentData = () => {
    const data = {};
    const phases = Array.isArray(aiReviewPhases) ? aiReviewPhases : (aiReviewPhases ? [aiReviewPhases] : []);
    for (const ph of phases) {
      if (ph === 'services') data.services = services;
      if (ph === 'processes') data.processes = processes;
      if (ph === 'cyberattack') data.cyberattack = cyberattackImpact;
    }
    return data;
  };

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const assessmentData = {
        services: services.map(s => ({
          service_name: s.service_name, service_description: s.service_description || null,
          user_count_score: s.user_count_score, frequency_score: s.frequency_score,
          replaceability_score: s.replaceability_score, interdependency_score: s.interdependency_score,
          time_to_impact_score: s.time_to_impact_score,
        })),
        processes: processes.map(p => ({
          process_name: p.process_name, process_description: p.process_description || null,
          automation_level: p.automation_level, it_ot_systems: p.it_ot_systems.join(', ') || null,
        })),
        asset_valuations: Object.keys(assetValuations).length > 0 ? assetValuations : undefined,
        cyberattack_impact: {
          security_impact: cyberattackImpact.security_impact,
          economy_impact: cyberattackImpact.economy_impact,
          health_impact: cyberattackImpact.health_impact,
          privacy_impact: cyberattackImpact.privacy_impact,
          notes: cyberattackImpact.notes || null,
        },
        source: 'admin_profile',
        notes: '',
      };
      const res = await oesApi.createAssessment(oesId, assessmentData);
      setSubmittedAssessment(res.data);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error submitting profile assessment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-eucnet-sky mx-auto" />
        </div>
      </Layout>
    );
  }

  if (success) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Card className="p-8 max-w-md text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">{t('assessment.assessmentSubmitted')}</h2>
            <p className="text-sm text-slate-500 mb-4">{t('assessment.criticalityAssessed', { entity: labels.entityName })}</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => navigate('/oes')} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200">{t('assessment.backToEntity', { entity: labels.entityName })}</button>
              <button onClick={() => navigate(`/oes/${oesId}/assessment-result`, { state: { assessment: submittedAssessment } })} className="px-4 py-2 text-sm font-medium text-white bg-eucnet-sky rounded-lg hover:bg-eucnet-navy">{t('assessment.viewResults')}</button>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const aiButton = (phase) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); handleAiPhase(phase); }}
      disabled={aiLoading}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white bg-eucnet-sky hover:bg-eucnet-navy rounded-md transition-colors disabled:opacity-50"
    >
      <AutoAwesome sx={{ fontSize: 13 }} />
      {aiLoading && aiPhase === phase ? t('assessment.analyzing') : t('settings.ai')}
    </button>
  );

  const distinctAssets = getDistinctAssets();

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {oes?.logo && (
              <img src={oes.logo} alt="" className="w-10 h-10 rounded-lg object-contain bg-slate-50 border border-slate-200" onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('entity.profiling', { entity: labels.entityName })}</p>
              <h1 className="text-xl font-bold text-eucnet-navy">{oes?.name || `${labels.entityName} Profile`}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAutoProfile}
              disabled={aiLoading}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-eucnet-navy hover:bg-eucnet-blue rounded-lg transition-colors disabled:opacity-50"
            >
              <AutoAwesome sx={{ fontSize: 16 }} />
              {aiLoading ? t('assessment.profilingPhase', { phase: aiPhase }) : t('assessment.autoProfile')}
            </button>
            <button onClick={() => navigate('/oes')} className="px-3 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">{t('common.cancel')}</button>
          </div>
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        {aiError && <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">{aiError}</div>}
        {existingAssessment && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>
              {t('assessment.existingAssessmentWarning', { entity: labels.entityName })}
              {existingAssessment.assessment_date && <> {t('assessment.submittedOn')} <strong>{new Date(existingAssessment.assessment_date).toLocaleDateString()}</strong></>}
              {existingAssessment.source === 'admin_profile' ? ` ${t('assessment.viaAdminProfiling')}` : ` ${t('assessment.viaSAQ')}`}
              {t('assessment.replaceWarning')}
            </span>
          </div>
        )}

        <div className="space-y-4">
          {/* Section 1: Services */}
          <Section title={t('assessment.essentialServices')} subtitle={t('assessment.essentialServicesSubtitle')} expanded={sections.services} onToggle={() => toggleSection('services')} aiButton={aiButton('services')}>
            <div className="space-y-4">
              {services.map((svc, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-slate-400">{svc.service_name?.trim() || `${t('assessment.service')} ${idx + 1}`}</span>
                    {services.length > 1 && <button onClick={() => removeService(idx)} className="text-[10px] text-red-500 hover:text-red-700">{t('common.remove')}</button>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input type="text" value={svc.service_name} onChange={(e) => updateService(idx, 'service_name', e.target.value)} placeholder={t('assessment.serviceName')} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-eucnet-sky" />
                    <input type="text" value={svc.service_description} onChange={(e) => updateService(idx, 'service_description', e.target.value)} placeholder={t('assessment.descriptionOptional')} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-eucnet-sky" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.reach')}</p>
                      <ScorePicker value={svc.user_count_score} onChange={(v) => updateService(idx, 'user_count_score', v)} labels={userCountLabels} />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.frequency')}</p>
                      <ScorePicker value={svc.frequency_score} onChange={(v) => updateService(idx, 'frequency_score', v)} labels={frequencyLabels} />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.replaceabilityLabel')}</p>
                      <ScorePicker value={svc.replaceability_score} onChange={(v) => updateService(idx, 'replaceability_score', v)} labels={replaceabilityLabels} />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.interdependency')}</p>
                      <ScorePicker value={svc.interdependency_score} onChange={(v) => updateService(idx, 'interdependency_score', v)} labels={interdependencyLabels} />
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.timeToImpact')}</p>
                      <ScorePicker value={svc.time_to_impact_score} onChange={(v) => updateService(idx, 'time_to_impact_score', v)} labels={timeToImpactLabels} />
                    </div>
                  </div>
                </div>
              ))}
              {services.length < 5 && (
                <button type="button" onClick={addService} className="text-xs text-eucnet-sky hover:text-eucnet-navy font-medium">{t('assessment.addService')}</button>
              )}
            </div>
          </Section>

          {/* Section 2: Processes */}
          <Section title={t('assessment.criticalProcesses')} subtitle={t('assessment.processesSubtitle')} expanded={sections.processes} onToggle={() => toggleSection('processes')} aiButton={aiButton('processes')}>
            <div className="space-y-4">
              {processes.map((proc, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-slate-400">{proc.process_name?.trim() || `${t('assessment.process')} ${idx + 1}`}</span>
                    {processes.length > 1 && <button onClick={() => removeProcess(idx)} className="text-[10px] text-red-500 hover:text-red-700">{t('common.remove')}</button>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <input type="text" value={proc.process_name} onChange={(e) => updateProcess(idx, 'process_name', e.target.value)} placeholder={t('assessment.processName')} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-eucnet-sky" />
                    <input type="text" value={proc.process_description} onChange={(e) => updateProcess(idx, 'process_description', e.target.value)} placeholder={t('assessment.descriptionOptional')} className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-eucnet-sky" />
                  </div>
                  <div className="mb-3">
                    <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.automationLevel')}</p>
                    <ScorePicker value={proc.automation_level} onChange={(v) => updateProcess(idx, 'automation_level', v)} labels={automationLabels} />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-slate-500 mb-1.5">{t('assessment.itOtSystemsLabel')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {IT_OT_SYSTEMS.map(sys => (
                        <button
                          key={sys}
                          type="button"
                          onClick={() => toggleItOt(idx, sys)}
                          className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                            proc.it_ot_systems.includes(sys)
                              ? 'bg-eucnet-sky text-white border-eucnet-sky'
                              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                          }`}
                        >
                          {sys}
                        </button>
                      ))}
                      {proc.it_ot_systems.filter(s => !IT_OT_SYSTEMS.includes(s)).map(custom => (
                        <span key={custom} className="px-2 py-0.5 text-[10px] rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">{custom}</span>
                      ))}
                    </div>
                    {otherInputProcessIndex === idx && (
                      <div className="flex gap-2 mt-2">
                        <input type="text" value={otherInputValue} onChange={(e) => setOtherInputValue(e.target.value)} placeholder={t('assessment.customItOtPlaceholder')} className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded" onKeyDown={(e) => e.key === 'Enter' && addCustomItOt(idx)} />
                        <button type="button" onClick={() => addCustomItOt(idx)} className="px-2 py-1 text-xs bg-eucnet-sky text-white rounded">{t('common.add')}</button>
                        <button type="button" onClick={() => setOtherInputProcessIndex(null)} className="px-2 py-1 text-xs text-slate-500 border rounded">{t('common.cancel')}</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {processes.length < 5 && (
                <button type="button" onClick={addProcess} className="text-xs text-eucnet-sky hover:text-eucnet-navy font-medium">{t('assessment.addProcess')}</button>
              )}
            </div>
          </Section>

          {/* Section 3: Asset Valuation */}
          <Section title={t('assessment.assetValuation')} subtitle={t('assessment.assetValuationSubtitle', { count: distinctAssets.length })} expanded={sections.assets} onToggle={() => toggleSection('assets')}>
            {distinctAssets.length === 0 ? (
              <p className="text-xs text-slate-400">{t('assessment.noAssetsMessage')}</p>
            ) : (
              <div className="space-y-2">
                {distinctAssets.map(asset => (
                  <div key={asset} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50/50 border border-slate-100">
                    <span className="text-xs font-medium text-slate-600 truncate">{asset}</span>
                    <ScorePicker value={assetValuations[asset] || 0} onChange={(v) => setAssetValuations(prev => ({ ...prev, [asset]: v }))} />
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Section 4: Cyberattack Impact */}
          <Section title={t('assessment.cyberattackImpactAssessment')} subtitle={t('assessment.cyberattackImpactSubtitle')} expanded={sections.cyberattack} onToggle={() => toggleSection('cyberattack')} aiButton={aiButton('cyberattack')}>
            <div className="space-y-3">
              {[
                { key: 'security_impact', label: t('assessment.impactNationalSecurity') },
                { key: 'economy_impact', label: t('assessment.impactEconomy') },
                { key: 'health_impact', label: t('assessment.impactHealth') },
                { key: 'privacy_impact', label: t('assessment.impactPrivacy') },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50/50 border border-slate-100">
                  <span className="text-xs font-medium text-slate-600">{label}</span>
                  <ScorePicker value={cyberattackImpact[key]} onChange={(v) => setCyberattackImpact(prev => ({ ...prev, [key]: v }))} labels={cyberLabels} />
                </div>
              ))}
              <div>
                <p className="text-[10px] font-medium text-slate-500 mb-1">{t('assessment.notesOptional')}</p>
                <textarea
                  value={cyberattackImpact.notes}
                  onChange={(e) => setCyberattackImpact(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-eucnet-sky"
                  placeholder={t('assessment.notesPlaceholder')}
                />
              </div>
            </div>
          </Section>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
          <button onClick={() => navigate('/oes')} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">{t('common.cancel')}</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !isDirty}
            className="px-6 py-2.5 text-sm font-medium text-white bg-eucnet-sky hover:bg-eucnet-navy rounded-lg transition-colors disabled:opacity-50"
          >
            {submitting ? t('common.submitting') : t('assessment.submitProfile')}
          </button>
        </div>
      </div>
      <AISuggestionReviewModal
        isOpen={aiReviewOpen}
        onClose={() => { setAiReviewOpen(false); setAiSuggestion(null); setAiReviewPhases(null); }}
        phase={aiReviewPhases}
        currentData={getAiReviewCurrentData()}
        aiData={aiSuggestion}
        onApply={handleApplyAiSuggestion}
      />
    </Layout>
  );
}
